import { filterNulls } from "replo-utils/lib/array";
import { z } from "zod";

import {
  getAgentPermissionToolLabel,
  getIntegrationToolPermissionTarget,
  REPLO_PRODUCT_TOOL_LABELS,
} from "./agentPermissions";
import { knownIntegrationToolMetadataRegistry } from "./agentTools/remote/integrations";

/**
 * What one gated tool call does, in the words a permission prompt, a chat row,
 * or a Slack approval message can show: the action, the thing it acts on, and
 * the few facts that decide whether the user wants it (price, status, budget).
 */
export type AgentPermissionCallSummary = {
  label: string;
  subject: string | null;
  /**
   * Set when the subject names a product the call identifies only by ID, so a
   * surface that looks products up knows which title the subject depends on.
   */
  subjectProductId?: string;
  facts: string[];
};

export type AgentPermissionIntegrationTool = {
  integrationKey: string;
  toolName: string;
  requiresPermission: boolean;
};

type CallInput = Record<string, unknown>;
type SummaryContext = {
  productTitleById: ReadonlyMap<string, string>;
};
type SummaryBuilder = (
  input: CallInput,
  context: SummaryContext,
) => Omit<AgentPermissionCallSummary, "label">;

const integrationToolByPermissionTarget = new Map<
  string,
  AgentPermissionIntegrationTool
>(
  Object.entries(knownIntegrationToolMetadataRegistry).flatMap(
    ([integrationKey, integration]) => {
      return Object.entries(integration.tools).map(([toolName, tool]) => [
        getIntegrationToolPermissionTarget({ integrationKey, toolName }),
        {
          integrationKey,
          toolName,
          requiresPermission:
            "requiresPermission" in tool && tool.requiresPermission === true,
        },
      ]);
    },
  ),
);

export function findIntegrationToolByPermissionTarget({
  permission,
}: {
  permission: string;
}): AgentPermissionIntegrationTool | null {
  return integrationToolByPermissionTarget.get(permission) ?? null;
}

const nonEmptyStringSchema = z.string().trim().min(1);
const recordSchema = z.record(z.string(), z.unknown());

function readString(input: CallInput, key: string): string | null {
  return nonEmptyStringSchema.safeParse(input[key]).data ?? null;
}

function readNumber(input: CallInput, key: string): number | null {
  return z.number().safeParse(input[key]).data ?? null;
}

function readRecord(input: CallInput, key: string): CallInput | null {
  return recordSchema.safeParse(input[key]).data ?? null;
}

function readArray(input: CallInput, key: string): unknown[] | null {
  return z.array(z.unknown()).safeParse(input[key]).data ?? null;
}

function readStringList(input: CallInput, key: string): string[] {
  return z.array(nonEmptyStringSchema).safeParse(input[key]).data ?? [];
}

function formatMajorUnits(amount: number) {
  return amount.toFixed(2);
}

function formatMinorUnits(amount: number) {
  return formatMajorUnits(amount / 100);
}

function humanizeEnumValue(value: string) {
  return value.replaceAll("_", " ").toLowerCase();
}

function excerpt(text: string, maxLength = 80) {
  const collapsed = text.replace(/\s+/g, " ").trim();
  return collapsed.length > maxLength
    ? `${collapsed.slice(0, maxLength - 1).trimEnd()}…`
    : collapsed;
}

function shortId(id: string) {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

function listNames(names: string[], max = 4) {
  if (names.length <= max) {
    return names.join(", ");
  }
  return `${names.slice(0, max).join(", ")} +${names.length - max} more`;
}

function countOf(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

// Product calls carry only the product's ID, so the title comes from whichever
// surface could look it up; a short ID stands in when none did.
function getProductSubject({
  input,
  context,
}: {
  input: CallInput;
  context: SummaryContext;
}) {
  const productId = readString(input, "productId");
  if (!productId) {
    return null;
  }
  return (
    context.productTitleById.get(productId) ?? `product ${shortId(productId)}`
  );
}

function getProductSubjectFields({
  input,
  context,
}: {
  input: CallInput;
  context: SummaryContext;
}) {
  return {
    subject: getProductSubject({ input, context }),
    subjectProductId: readString(input, "productId") ?? undefined,
  };
}

function getVariantFacts(input: CallInput) {
  const variants = readArray(input, "variants");
  if (!variants) {
    return [];
  }
  const prices = filterNulls(
    variants.map((variant) => {
      const record = recordSchema.safeParse(variant).data;
      return record ? readNumber(record, "price") : null;
    }),
  );
  const minimumPrice = prices.length > 0 ? Math.min(...prices) : null;
  return filterNulls([
    countOf(variants.length, "variant"),
    minimumPrice === null ? null : `from ${formatMajorUnits(minimumPrice)}`,
  ]);
}

const PRODUCT_UPDATE_FIELD_LABELS: Record<string, string> = {
  title: "title",
  description: "description",
  slug: "page URL",
  status: "status",
  externalCheckoutUrl: "checkout link",
};

function getMetaBudgetFact(input: CallInput) {
  const dailyBudget = readNumber(input, "dailyBudget");
  if (dailyBudget !== null) {
    return `daily budget ${formatMinorUnits(dailyBudget)}`;
  }
  const lifetimeBudget = readNumber(input, "lifetimeBudget");
  return lifetimeBudget === null
    ? null
    : `lifetime budget ${formatMinorUnits(lifetimeBudget)}`;
}

function getMetaStatusFact(input: CallInput) {
  return humanizeEnumValue(readString(input, "status") ?? "PAUSED");
}

function getMetaTargetingFact(input: CallInput) {
  const targeting = readRecord(input, "targeting");
  if (!targeting) {
    return null;
  }
  const countries = readStringList(targeting, "countries");
  const ageMin = readNumber(targeting, "ageMin") ?? 18;
  const ageMax = readNumber(targeting, "ageMax");
  const ages = ageMax === null ? `ages ${ageMin}+` : `ages ${ageMin}-${ageMax}`;
  return countries.length > 0 ? `${listNames(countries)} · ${ages}` : ages;
}

function getTextExcerptFact(input: CallInput) {
  for (const key of ["text", "message", "body", "description", "content"]) {
    const value = readString(input, key);
    if (value) {
      return `"${excerpt(value)}"`;
    }
  }
  return null;
}

// Keyed "<integrationKey>:<toolName>". Anything not listed falls back to the
// generic name/status/excerpt reading below, which is enough for most writes.
const summaryBuilderByTool: Record<string, SummaryBuilder> = {
  "replo-products:products_create": (input) => {
    const price = readNumber(input, "price");
    return {
      subject: readString(input, "title"),
      facts: [
        ...(price === null
          ? getVariantFacts(input)
          : [formatMajorUnits(price)]),
        readString(input, "status") ?? "draft",
      ],
    };
  },
  "replo-products:products_create_from_catalog": (input) => {
    const providerProductId = readString(input, "providerProductId");
    const colors = [
      ...readStringList(input, "sellColorNames"),
      ...readStringList(input, "colors"),
    ];
    return {
      subject:
        readString(input, "title") ??
        (providerProductId ? `catalog item ${providerProductId}` : null),
      facts: filterNulls([
        colors.length > 0 ? listNames(colors) : null,
        "print-on-demand draft",
        "paid orders print and ship automatically",
      ]),
    };
  },
  "replo-products:products_update": (input, context) => {
    const changedFields = Object.keys(PRODUCT_UPDATE_FIELD_LABELS)
      .filter((key) => key in input)
      .map((key) => PRODUCT_UPDATE_FIELD_LABELS[key]);
    const status = readString(input, "status");
    const title = readString(input, "title");
    return {
      ...(title
        ? { subject: title }
        : getProductSubjectFields({ input, context })),
      facts: filterNulls([
        changedFields.length > 0 ? `changes ${changedFields.join(", ")}` : null,
        status ? `status ${status}` : null,
        "live pages update immediately",
      ]),
    };
  },
  "replo-products:products_delete": (input, context) => ({
    ...getProductSubjectFields({ input, context }),
    facts: ["permanent", "gone from live pages immediately"],
  }),
  "replo-products:products_set_variants": (input, context) => {
    const optionNames = filterNulls(
      (readArray(input, "options") ?? []).map((option) => {
        const record = recordSchema.safeParse(option).data;
        return record ? readString(record, "name") : null;
      }),
    );
    const renames = readArray(input, "renameOptionValues")?.length ?? 0;
    const removals = readArray(input, "removeOptionValues")?.length ?? 0;
    const restores = readArray(input, "restoreOptionValues")?.length ?? 0;
    return {
      ...getProductSubjectFields({ input, context }),
      facts: filterNulls([
        optionNames.length > 0 ? `options ${listNames(optionNames)}` : null,
        optionNames.length > 0 ? "anything left out stops selling" : null,
        ...getVariantFacts(input),
        renames > 0 ? countOf(renames, "rename") : null,
        removals > 0
          ? countOf(removals, "value removed", "values removed")
          : null,
        restores > 0
          ? countOf(restores, "value restored", "values restored")
          : null,
      ]),
    };
  },
  "replo-products:products_set_inventory": (input, context) => ({
    ...getProductSubjectFields({ input, context }),
    facts: [countOf(readArray(input, "variants")?.length ?? 0, "variant")],
  }),
  "replo-products:products_set_media": (input, context) => {
    const mediaCount = readArray(input, "media")?.length ?? 0;
    return {
      ...getProductSubjectFields({ input, context }),
      facts: [
        mediaCount === 0 ? "removes all photos" : countOf(mediaCount, "photo"),
      ],
    };
  },
  "replo-products:products_duplicate": (input, context) => {
    const title = readString(input, "title");
    const source = getProductSubjectFields({ input, context });
    return {
      subject: title ?? (source.subject ? `copy of ${source.subject}` : null),
      subjectProductId: title ? undefined : source.subjectProductId,
      facts: filterNulls([
        title && source.subject ? `copy of ${source.subject}` : null,
        "draft",
      ]),
    };
  },
  "replo-products:products_place_design": (input, context) => {
    const widthPx = readNumber(input, "widthPx");
    const heightPx = readNumber(input, "heightPx");
    return {
      subject: readString(input, "placementKey"),
      facts: filterNulls([
        getProductSubject({ input, context }),
        widthPx !== null && heightPx !== null
          ? `${widthPx}×${heightPx} px`
          : null,
      ]),
    };
  },
  "replo-products:products_render_mockups": (input, context) => ({
    ...getProductSubjectFields({ input, context }),
    facts: [],
  }),
  "replo-products:products_save_mockups": (input, context) => ({
    ...getProductSubjectFields({ input, context }),
    facts: [],
  }),
  "replo-products:orders_create_draft": (input) => ({
    subject: readString(input, "externalReference"),
    facts: ["unconfirmed draft", "no charge"],
  }),
  "meta-marketing-api:create_campaign": (input) => {
    const objective = readString(input, "objective");
    return {
      subject: readString(input, "name"),
      facts: filterNulls([
        objective ? humanizeEnumValue(objective) : null,
        getMetaBudgetFact(input),
        getMetaStatusFact(input),
      ]),
    };
  },
  "meta-marketing-api:create_ad_set": (input) => {
    const optimizationGoal = readString(input, "optimizationGoal");
    return {
      subject: readString(input, "name"),
      facts: filterNulls([
        getMetaBudgetFact(input),
        getMetaTargetingFact(input),
        optimizationGoal ? humanizeEnumValue(optimizationGoal) : null,
        getMetaStatusFact(input),
      ]),
    };
  },
  "meta-marketing-api:create_ad": (input) => ({
    subject: readString(input, "name"),
    facts: [getMetaStatusFact(input)],
  }),
  "meta-marketing-api:create_ad_creative": (input) => ({
    subject: readString(input, "name"),
    facts: filterNulls([
      readString(input, "headline"),
      readString(input, "link"),
    ]),
  }),
  "google-ads:create_campaign_budget": (input) => {
    const amountMicros = readNumber(input, "amountMicros");
    return {
      subject: readString(input, "name"),
      facts: filterNulls([
        amountMicros === null
          ? null
          : `${formatMajorUnits(amountMicros / 1_000_000)} per day`,
      ]),
    };
  },
  "opus:social_publish": (input) => ({
    subject: readString(input, "title") ?? readString(input, "clipId"),
    facts: filterNulls([
      `account ${readString(input, "postAccountId") ?? "unknown"}`,
      getTextExcerptFact(input),
      "posts publicly now",
    ]),
  }),
  "opus:social_schedule": (input) => ({
    subject: readString(input, "title") ?? readString(input, "clipId"),
    facts: filterNulls([
      `account ${readString(input, "postAccountId") ?? "unknown"}`,
      readString(input, "scheduledAtUtc"),
      getTextExcerptFact(input),
    ]),
  }),
  "slack:send_message": (input) => ({
    subject: readString(input, "channel"),
    facts: filterNulls([
      getTextExcerptFact(input),
      readString(input, "thread_ts") ? "thread reply" : null,
    ]),
  }),
};

function getGenericSummary(input: CallInput) {
  const subject = ["title", "name", "subject", "label", "channel"]
    .map((key) => readString(input, key))
    .find((value) => value !== null);
  const status = readString(input, "status");
  return {
    subject: subject ?? null,
    facts: filterNulls([
      status ? humanizeEnumValue(status) : null,
      getTextExcerptFact(input),
    ]),
  };
}

function isReploProductToolName(
  toolName: string,
): toolName is keyof typeof REPLO_PRODUCT_TOOL_LABELS {
  return Object.hasOwn(REPLO_PRODUCT_TOOL_LABELS, toolName);
}

export function getAgentPermissionCallLabel({
  tool,
}: {
  tool: AgentPermissionIntegrationTool;
}) {
  return tool.integrationKey === "replo-products" &&
    isReploProductToolName(tool.toolName)
    ? REPLO_PRODUCT_TOOL_LABELS[tool.toolName]
    : getAgentPermissionToolLabel({ toolName: tool.toolName });
}

/**
 * Null when the permission is not an integration tool (bash, edit, publish…):
 * those prompts describe patterns, not a call.
 */
export function getAgentPermissionCallSummary({
  permission,
  input,
  productTitleById = new Map(),
}: {
  permission: string;
  input: CallInput | null | undefined;
  productTitleById?: ReadonlyMap<string, string>;
}): AgentPermissionCallSummary | null {
  const tool = findIntegrationToolByPermissionTarget({ permission });
  if (!tool) {
    return null;
  }
  const build =
    summaryBuilderByTool[`${tool.integrationKey}:${tool.toolName}`] ??
    getGenericSummary;
  return {
    label: getAgentPermissionCallLabel({ tool }),
    ...build(input ?? {}, { productTitleById }),
  };
}

export function formatAgentPermissionCallSummary({
  summary,
}: {
  summary: AgentPermissionCallSummary;
}) {
  return filterNulls([summary.subject, ...summary.facts]).join(" · ");
}

// "{count}" stands in for the batch size.
const pluralScopeByTool: Record<string, string> = {
  "replo-products:products_create": "Create {count} products",
  "replo-products:products_create_from_catalog":
    "Add {count} print-on-demand products",
  "replo-products:products_update": "Change {count} products",
  "replo-products:products_delete": "Delete {count} products",
  "replo-products:products_duplicate": "Duplicate {count} products",
  "replo-products:products_set_variants":
    "Change options and variants on {count} products",
  "replo-products:products_set_inventory": "Change stock on {count} products",
  "replo-products:products_set_media": "Change photos on {count} products",
  "replo-products:products_place_design": "Put designs on {count} placements",
  "replo-products:products_render_mockups":
    "Generate mockups for {count} products",
  "replo-products:products_save_mockups": "Save mockups for {count} products",
  "meta-marketing-api:create_campaign": "Create {count} campaigns",
  "meta-marketing-api:create_ad_set": "Create {count} ad sets",
  "meta-marketing-api:create_ad": "Create {count} ads",
  "meta-marketing-api:create_ad_creative": "Create {count} ad creatives",
  "slack:send_message": "Send {count} Slack messages",
};

/** "Create 3 products" for a batch; the single-call label otherwise. */
export function getAgentPermissionScopeTitle({
  permission,
  count,
}: {
  permission: string;
  count: number;
}): string | null {
  const tool = findIntegrationToolByPermissionTarget({ permission });
  if (!tool) {
    return null;
  }
  const label = getAgentPermissionCallLabel({ tool });
  if (count <= 1) {
    return label;
  }
  const pluralScope =
    pluralScopeByTool[`${tool.integrationKey}:${tool.toolName}`];
  return pluralScope
    ? pluralScope.replace("{count}", String(count))
    : `${label} (${count} calls)`;
}
